import { Autocomplete, TextField } from "@mui/material";
import axios, { AxiosResponse } from "axios";
import React from "react";
import { AppContext, User } from "../../../context/appContext";
import { endpoints } from "../../../endpoints/endpoints";
import ParticipantSearchProps from "../interface/participantSearchProps";
import SelectableParticipant from "../interface/selectableParticipant";

const MINIMUM_SEARCH_LENGTH = 2;

const RenderParticipantSearch = ({
  participantSearch,
  setParticipantSearch,
}: {
  participantSearch: ParticipantSearchProps;
  setParticipantSearch: React.Dispatch<
    React.SetStateAction<ParticipantSearchProps>
  >;
}): React.JSX.Element => {
  const {
    user,
    checkSession,
  }: {
    user: User;
    checkSession: (response: AxiosResponse) => void;
  } = React.useContext(AppContext);

  const startSearch = (searchString: string): void => {
    setParticipantSearch((currentSearch) => ({
      ...currentSearch,
      loading: true,
    }));

    axios
      .post(endpoints.admin.searchParticipant, {
        sessionId: user.sessionId,
        searchString: searchString,
      })
      .then((response) => {
        checkSession(response);

        setParticipantSearch((currentSearch) => ({
          ...currentSearch,
          loading: false,
          searchResults:
            response.data.meta.status === "success" ? response.data.data : [],
        }));
      })
      .catch((error) => {
        console.error("Error searching participants", error);
        setParticipantSearch((currentSearch) => ({
          ...currentSearch,
          loading: false,
          searchResults: [],
        }));
      });
  };

  const buildLabel = (option: SelectableParticipant): string =>
    `${option.firstName} ${option.lastName} (${option.email})`;

  return (
    <Autocomplete
      fullWidth
      loading={participantSearch.loading}
      loadingText="Suche..."
      disablePortal
      noOptionsText="Bitte mindestens 2 Zeichen eingeben."
      id="searchParticipantForBooking"
      options={participantSearch.searchResults}
      getOptionLabel={buildLabel}
      value={participantSearch.selectedParticipant}
      isOptionEqualToValue={(option, value) =>
        option.participantId === value.participantId
      }
      onInputChange={(_event, value) => {
        setParticipantSearch((currentSearch) => ({
          ...currentSearch,
          searchString: value,
        }));

        if (participantSearch.loading) return;
        if (value.length < MINIMUM_SEARCH_LENGTH) return;

        startSearch(value);
      }}
      onChange={(_event, option: SelectableParticipant | null) => {
        setParticipantSearch((currentSearch) => ({
          ...currentSearch,
          selectedParticipant: option,
        }));
      }}
      renderInput={(params) => (
        /*
        The minimum length is stated in noOptionsText: MUI resets inputValue to "" right
        after each keystroke, so an error derived from the typed string would flicker and
        would show on an untouched field.
        */
        <TextField {...params} color="secondary" label="Teilnehmer:in suchen" />
      )}
    />
  );
};

export default RenderParticipantSearch;
